import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { VxIconComponent } from '@ui/icon/icon.component';
import { interval, map, startWith } from 'rxjs';
import { VTooltipDirective } from 'src/app/core/components/ui-kit/tooltip/tooltip.directive';
import { DurationPipe } from 'src/app/core/pipes/duration.pipe';
import { LocaleDatePipe } from 'src/app/core/pipes/locale-date.pipe';
import { fullName } from 'src/app/models/user';
import { ImpersonationService } from 'src/app/services/impersonation/impersonation.service';
import { UserState } from 'src/app/store/user/user.state';
import { impersonationBannerVariants } from './impersonation-banner.variants';

@Component({
  selector: 'app-impersonation-banner',
  templateUrl: './impersonation-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    VxIconComponent,
    VTooltipDirective,
    DurationPipe,
    LocaleDatePipe,
  ],
})
export class ImpersonationBannerComponent {
  private impersonationService = inject(ImpersonationService);

  protected impersonation = this.impersonationService.impersonation;
  /** Also while the session's details are unknown (the check failed). */
  protected active = this.impersonationService.active;
  protected user = inject(Store).selectSignal(UserState.getUser);
  protected ending = signal(false);

  protected userName = computed(() => {
    const user = this.user();
    return fullName(user) || user?.email || '';
  });

  private now = toSignal(
    interval(15_000).pipe(
      startWith(0),
      map(() => Date.now())
    ),
    { initialValue: Date.now() }
  );

  /** Whole minutes left, rounded up so the bar never shows 0 while active. */
  protected remainingSeconds = computed(() => {
    const expiresAt = this.impersonation()?.expiresAt.getTime() ?? 0;
    return Math.max(0, Math.ceil((expiresAt - this.now()) / 60_000) * 60);
  });

  // tv() slots are functions — resolve them to strings before binding
  protected classes = computed(() => {
    // unknown details count as the riskier mode: changes may be allowed
    const slots = impersonationBannerVariants({
      mode: this.impersonation()?.readOnly ? 'readOnly' : 'changesAllowed',
    });
    return {
      frame: slots.frame(),
      tab: slots.tab(),
      muted: slots.muted(),
      end: slots.end(),
    };
  });

  protected end() {
    this.ending.set(true);
    void this.impersonationService.end();
  }
}
